import { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Add, ErrorOutline } from '@material-ui/icons';
import { observer } from 'mobx-react';
import {
  Button,
  EXTERNAL_MANUAL_REVIEW_BANNER_TEXT,
  EXTERNAL_PROCESSING_BANNER_TEXT,
  PDFPreview,
  StatusDiv,
  processingStatusBannerText,
} from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { CustomTaskDialog, useGeneralTasksStore } from '@roc/feature-general-tasks';
import { Document } from '@roc/feature-types';
import { useLienPayoffReviewStore } from '../../hooks/useLienPayoffReviewStore';
import {
  LienPayoffReviewCard,
  PropertyLienPayoff,
  getLetterStatus,
  hasAddressMismatch,
  hasEvidence,
} from './lienPayoffReviewCard';
import { AddEditLienModal } from './addEditLienModal';

type Banner =
  | { kind: 'status'; status: string; text: string }
  | { kind: 'review'; items: string[] }
  | null;

interface LienPayoffReviewDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document;
}

const useStyles = makeStyles(theme => ({
  banner: {
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  addButton: {
    textTransform: 'none',
    fontWeight: 600,
  },
  mismatchBanner: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    color: '#991B1B',
  },
  mismatchTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  mismatchIcon: {
    fontSize: '1.1rem',
  },
  mismatchText: {
    fontWeight: 700,
    fontSize: '0.875rem',
    color: 'inherit',
  },
  mismatchList: {
    margin: theme.spacing(1, 0, 0),
    paddingLeft: theme.spacing(5),
  },
  payoffAmount: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
}));

export const LienPayoffReviewContent = observer(
  ({
    records,
    onRefresh,
    readOnly = false,
    internalView = false,
  }: {
    records: PropertyLienPayoff[];
    onRefresh: () => void;
    readOnly?: boolean;
    internalView?: boolean;
  }) => {
    const classes = useStyles();
    const { lienPayoffReviewStore } = useLienPayoffReviewStore();
    const [addLienOpen, setAddLienOpen] = useState(false);

    // Sum of the lien amounts - the same total the backend pushes to the collateral's refi amount.
    const payoffAmount = useMemo(() => {
      const amounts = records
        .map(record => record.lien.originalAmount)
        .filter((amount): amount is number => amount != null);
      return amounts.length ? amounts.reduce((sum, amount) => sum + amount, 0) : null;
    }, [records]);

    // "Payoff N" comes from the lien's stored payoffSequence; creation order is only a fallback
    // for a lien that hasn't been numbered yet. Rows are shown in that order, Payoff 1 first.
    const ordinalByLienId = useMemo(() => {
      const liens = records
        .map(record => record.lien)
        .sort((a, b) => a.propertyLienId - b.propertyLienId);
      return new Map(liens.map((lien, position) => [lien.propertyLienId, lien.payoffSequence ?? position + 1]));
    }, [records]);

    const orderedRecords = useMemo(
      () =>
        [...records].sort(
          (a, b) =>
            (ordinalByLienId.get(a.lien.propertyLienId) ?? 0) -
            (ordinalByLienId.get(b.lien.propertyLienId) ?? 0) ||
            a.lien.propertyLienId - b.lien.propertyLienId
        ),
      [records, ordinalByLienId]
    );

    // Payoffs that need a person: a letter that couldn't be read, or an uploaded letter whose address
    // doesn't match the property (its amount isn't used).
    const reviewItems = useMemo(
      () =>
        orderedRecords
          .map(record => {
            const label = `Payoff ${ordinalByLienId.get(record.lien.propertyLienId) ?? ''}`;
            const letterStatus = getLetterStatus(record.payoffRecord);
            if (letterStatus === 'FAILED') return `${label}: the letter couldn't be read, please re-upload it`;
            if (letterStatus !== 'IN_PROGRESS' && hasEvidence(record) && hasAddressMismatch(record)) {
              return `${label}: the address doesn't match the property, so the letter's amount wasn't used`;
            }
            return null;
          })
          .filter((item): item is string => item != null),
      [orderedRecords, ordinalByLienId]
    );

    // One status for the whole Payoff task: Processing while any letter is still being extracted,
    // else Failed / Flagged when any letter failed / needs review, else ready.
    const processingStatus = useMemo(() => {
      const statuses = records.map(record => getLetterStatus(record.payoffRecord)).filter(Boolean);
      if (statuses.includes('IN_PROGRESS')) return 'IN_PROGRESS';
      if (statuses.includes('FAILED')) return 'FAILED';
      if (statuses.includes('FLAGGED')) return 'FLAGGED';
      return statuses.length ? 'SUCCESS' : null;
    }, [records]);

    // Only one banner at a time: Processing, then the payoffs to review, then Flagged / ready.
    // The TPO never sees the review details, only a generic manual review banner. The read-only
    // embed shows just the review list.
    const banner = useMemo((): Banner => {
      if (readOnly) return reviewItems.length ? { kind: 'review', items: reviewItems } : null;
      if (!internalView) {
        if (processingStatus === 'IN_PROGRESS') {
          return { kind: 'status', status: 'IN_PROGRESS', text: EXTERNAL_PROCESSING_BANNER_TEXT };
        }
        return reviewItems.length || processingStatus === 'FLAGGED'
          ? { kind: 'status', status: 'FAILED', text: EXTERNAL_MANUAL_REVIEW_BANNER_TEXT }
          : null;
      }
      if (processingStatus === 'IN_PROGRESS') {
        return { kind: 'status', status: 'IN_PROGRESS', text: processingStatusBannerText.IN_PROGRESS };
      }
      if (reviewItems.length) return { kind: 'review', items: reviewItems };
      return processingStatus
        ? { kind: 'status', status: processingStatus, text: processingStatusBannerText[processingStatus] }
        : null;
    }, [readOnly, internalView, processingStatus, reviewItems]);

    return (
      <Grid container>
        <Grid item xs={12}>
          {banner?.kind === 'status' && (
            <Box className={classes.banner}>
              <StatusDiv
                status={banner.status}
                text={banner.text}
                onRefresh={internalView ? onRefresh : undefined}
              />
            </Box>
          )}
          {banner?.kind === 'review' && (
            <Box className={`${classes.banner} ${classes.mismatchBanner}`}>
              <Box className={classes.mismatchTitle}>
                <ErrorOutline className={classes.mismatchIcon} />
                <Typography className={classes.mismatchText}>Please review the following payoffs:</Typography>
              </Box>
              <ul className={classes.mismatchList}>
                {banner.items.map(item => (
                  <li key={item}>
                    <Typography className={classes.mismatchText}>{item}</Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}
          <Box className={classes.sectionHeader}>
            <Box>
              {internalView && (
                <Typography className={classes.payoffAmount}>
                  {`Payoff Amount: ${payoffAmount != null ? formatCurrency(payoffAmount) : '-'} (${records.length})`}
                </Typography>
              )}
              <Typography>
                Please provide supporting evidence for each of the payoff items requested below:
              </Typography>
            </Box>
            {internalView && !readOnly && (
              <Button
                className={classes.addButton}
                variant="contained"
                color="primary"
                size="small"
                startIcon={<Add />}
                onClick={() => setAddLienOpen(true)}
                testId="add-payoff-lien-button"
              >
                Add Payoff
              </Button>
            )}
          </Box>
          {orderedRecords.map(record => (
            <LienPayoffReviewCard
              key={record.lien.propertyLienId}
              record={record}
              ordinal={ordinalByLienId.get(record.lien.propertyLienId) ?? 0}
              readOnly={readOnly}
              internalView={internalView}
            />
          ))}
        </Grid>
        {addLienOpen && (
          <AddEditLienModal
            open={addLienOpen}
            onClose={() => setAddLienOpen(false)}
            editingLienId={null}
          />
        )}
        <PDFPreview
          open={lienPayoffReviewStore.letterPreview != null}
          title={lienPayoffReviewStore.letterPreview?.title}
          data={lienPayoffReviewStore.letterPreview?.data}
          dataV2={lienPayoffReviewStore.letterPreview?.data}
          headers={lienPayoffReviewStore.letterPreview?.headers}
          onClose={() => lienPayoffReviewStore.closeLetterPreview()}
        />
      </Grid>
    );
  }
);

export const LienPayoffReviewDialog = observer(
  ({ open, onClose, document }: LienPayoffReviewDialogProps) => {
    const { lienPayoffReviewStore, globalStore } = useLienPayoffReviewStore();
    const { generalTasksStore } = useGeneralTasksStore();
    const taskId = document?.taskId;
    // Internal portal (underwriting): extracted data, Add/Edit Payoff. TPO (external portal): upload
    // only, extracted data hidden. TPO notes go through the task chat.
    const internalView = globalStore.isInternalPortal;
    const loanId = document?.loanId;
    // Payoff is a per-property task (type2 = PROPERTY_ID), so collateralId
    // is that property's id - scopes the liens shown to this property only.
    const propertyId = document?.collateralId ? Number(document.collateralId) : undefined;

    useEffect(() => {
      if (open && loanId) {
        lienPayoffReviewStore.getScreenData(loanId, propertyId);
      }
    }, [open, loanId, propertyId]);

    const dialogContent = (
      <LienPayoffReviewContent
        records={lienPayoffReviewStore.records}
        onRefresh={() => loanId && lienPayoffReviewStore.getScreenData(loanId, propertyId)}
        internalView={internalView}
      />
    );

    // Same cleanup as the dialog's own X, so the next open starts on the Payoff tab.
    const handleClose = () => {
      generalTasksStore.setOpenChatTabForTaskId(null);
      onClose();
    };

    const dialogActions = (
      <Grid container justifyContent="flex-end">
        <Grid item>
          <Button onClick={handleClose} color="primary" variant="outlined" testId="close">
            Close
          </Button>
        </Grid>
      </Grid>
    );

    return <>
      {taskId && (
        <CustomTaskDialog
          open={open}
          title="Payoff"
          maxWidth="lg"
          handleClose={onClose}
          dialogContent={dialogContent}
          dialogActions={dialogActions}
          taskId={taskId}
        />)}
    </>
  }
);
